import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { exportAPI } from '../../lib/apiRoutes';
import { useAuthStore } from '../../lib/store';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Ticket, Route, Truck, Map, Download,
  TrendingUp, CheckCircle2, XCircle, Clock, UserX, MapPin, Globe,
  MessageSquare, Phone, FolderOpen, PenLine, ChevronLeft, ChevronRight, Package,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  notificada:  { color: 'bg-blue-50 border-blue-200',   text: 'text-blue-700',   dot: 'bg-blue-500',   label: 'Notificadas' },
  asignada:    { color: 'bg-amber-50 border-amber-200',  text: 'text-amber-700',  dot: 'bg-amber-500',  label: 'Asignadas' },
  realizada:   { color: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Realizadas' },
  cancelada:   { color: 'bg-red-50 border-red-200',     text: 'text-red-700',    dot: 'bg-red-500',    label: 'Canceladas' },
  ausente:     { color: 'bg-slate-50 border-slate-200', text: 'text-slate-600',  dot: 'bg-slate-400',  label: 'Ausentes' },
  pendiente:   { color: 'bg-orange-50 border-orange-200', text: 'text-orange-700', dot: 'bg-orange-400', label: 'Pendientes' },
};

const CANAL_ICONS = {
  web:       <Globe className="w-4 h-4" />,
  whatsapp:  <MessageSquare className="w-4 h-4" />,
  ivr:       <Phone className="w-4 h-4" />,
  historico: <FolderOpen className="w-4 h-4" />,
  manual:    <PenLine className="w-4 h-4" />,
};

// ─── Weekly Chart Tooltip ─────────────────────────────────────────────────────
const WeekTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 text-sm">
      <p className="font-bold text-slate-900 mb-1">{d?.dia_semana} {d?.dia_num} {d?.mes}</p>
      <p className="text-teal-700 flex items-center gap-1"><Package className="w-3.5 h-3.5" />{d?.total_enseres} enseres</p>
      <p className="text-slate-600 flex items-center gap-1"><Ticket className="w-3.5 h-3.5" />{d?.total_tickets} tickets</p>
      {d?.realizadas > 0 && <p className="text-emerald-600 text-xs mt-1">{d.realizadas} ya realizadas</p>}
    </div>
  );
};

// ─── Weekly Planning Chart ────────────────────────────────────────────────────
const SemanaCargaChart = () => {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = async (off) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/tickets/semana-stats?semana_offset=${off}`);
      const d = await res.json();
      setData(d);
    } catch (_) {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(offset); }, [offset]);

  const maxEnseres = data ? Math.max(...data.dias.map(d => d.total_enseres), 1) : 1;

  const getBarColor = (dia) => {
    if (dia.es_fin_semana) return '#e2e8f0';
    if (dia.es_hoy) return '#0d9488';
    if (dia.total_enseres === 0) return '#f1f5f9';
    const ratio = dia.total_enseres / maxEnseres;
    if (ratio > 0.8) return '#0f766e';
    if (ratio > 0.5) return '#14b8a6';
    return '#5eead4';
  };

  const semanaLabel = offset === 0
    ? 'Próximas 2 semanas'
    : offset < 0
      ? `Hace ${Math.abs(offset)} semana${Math.abs(offset) > 1 ? 's' : ''}`
      : `En ${offset} semana${offset > 1 ? 's' : ''}`;

  return (
    <Card className="border-slate-200 shadow-none" data-testid="semana-carga-chart">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Package className="w-4 h-4" /> Planificación de Carga — Enseres por Día
            </CardTitle>
            <p className="text-xs text-slate-400 mt-0.5">{semanaLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            {data && (
              <span className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1">
                <span className="font-bold text-slate-800">{data.total_enseres}</span> enseres ·{' '}
                <span className="font-bold text-slate-800">{data.total_tickets}</span> tickets
              </span>
            )}
            <button onClick={() => setOffset(o => o - 1)} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors" data-testid="semana-prev">
              <ChevronLeft className="w-4 h-4 text-slate-500" />
            </button>
            <button
              onClick={() => setOffset(0)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${offset === 0 ? 'bg-teal-600 text-white border-teal-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              data-testid="semana-hoy"
            >
              Hoy
            </button>
            <button onClick={() => setOffset(o => o + 1)} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors" data-testid="semana-next">
              <ChevronRight className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-48 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-teal-600 border-t-transparent" />
          </div>
        ) : data ? (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.dias} margin={{ top: 4, right: 8, bottom: 0, left: -20 }} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="dia_num"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <Tooltip content={<WeekTooltip />} cursor={{ fill: '#f8fafc' }} />
                <Bar dataKey="total_enseres" radius={[4, 4, 0, 0]} maxBarSize={48}>
                  {data.dias.map((dia, idx) => (
                    <Cell key={idx} fill={getBarColor(dia)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Day labels */}
            <div className="flex mt-1" style={{ paddingLeft: '30px', paddingRight: '8px' }}>
              {data.dias.map((dia, idx) => (
                <div key={idx} className="flex-1 text-center">
                  <p className={`text-xs font-semibold ${dia.es_hoy ? 'text-teal-700' : dia.es_fin_semana ? 'text-slate-300' : 'text-slate-500'}`}>
                    {dia.dia_semana}
                  </p>
                  <p className={`text-xs ${dia.es_hoy ? 'text-teal-600 font-bold' : 'text-slate-400'}`}>
                    {dia.dia_num}/{dia.mes}
                  </p>
                  {dia.es_hoy && <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mx-auto mt-0.5" />}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex gap-4 mt-3 pt-3 border-t border-slate-100 flex-wrap">
              {[
                { color: '#0d9488', label: 'Hoy' },
                { color: '#0f766e', label: 'Carga alta' },
                { color: '#14b8a6', label: 'Carga media' },
                { color: '#5eead4', label: 'Carga baja' },
                { color: '#e2e8f0', label: 'Fin de semana' },
              ].map(({ color, label }) => (
                <div key={label} className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                  {label}
                </div>
              ))}
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

// ─── Main Dashboard Component ─────────────────────────────────────────────────
const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total: 0, pendiente: 0, notificada: 0, asignada: 0,
    realizada: 0, cancelada: 0, ausente: 0, porLugar: {}, porProcedencia: {}
  });
  const [loading, setLoading] = useState(true);
  const [exportFilters, setExportFilters] = useState({ fecha_desde: '', fecha_hasta: '', estado: '', lugar: '' });
  const [showExportModal, setShowExportModal] = useState(false);
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || !user?.role;

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(`${API}/api/stats/dashboard`);
      const data = await response.json();
      const estados = data.por_estado || {};
      setStats({
        total: data.total || 0,
        pendiente: (estados['Pendiente'] || 0) + (estados['En espera'] || 0),
        notificada: estados['Notificada'] || 0,
        asignada: estados['Asignada'] || 0,
        realizada: estados['Realizada'] || 0,
        cancelada: estados['Cancelada'] || 0,
        ausente: estados['Ausente'] || 0,
        porLugar: data.por_lugar || {},
        porProcedencia: data.por_procedencia || {}
      });
    } catch (e) { console.error('Error loading stats:', e); }
    finally { setLoading(false); }
  };

  const handleExport = () => {
    exportAPI.ticketsXLSX(exportFilters);
    setShowExportModal(false);
  };

  const zonas = ['Cádiz', 'Casco Antiguo', 'Puerta Tierra'];
  const estados = ['Notificada', 'Asignada', 'Realizada', 'Cancelada', 'Ausente'];

  const maxLugar = Math.max(...Object.values(stats.porLugar), 1);
  const totalProcedencia = Object.values(stats.porProcedencia).reduce((a, b) => a + b, 0) || 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-teal-600 border-t-transparent mx-auto mb-3"></div>
          <p className="text-sm text-slate-500">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-0.5">Panel de Control</p>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Resumen en tiempo real de la actividad de recogidas</p>
        </div>
        <Button
          onClick={() => setShowExportModal(true)}
          variant="outline"
          className="border-slate-200 hover:border-teal-400 hover:text-teal-700"
          data-testid="export-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Exportar Excel
        </Button>
      </div>

      {/* Total KPI Banner */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-5 mb-5 text-white flex items-center justify-between" data-testid="kpi-total">
        <div>
          <p className="text-teal-200 text-sm font-medium uppercase tracking-wider">Total Tickets en el Sistema</p>
          <p className="text-5xl font-bold mt-1">{stats.total.toLocaleString('es-ES')}</p>
        </div>
        <TrendingUp className="w-12 h-12 text-teal-300 opacity-50" />
      </div>

      {/* Status KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <div
            key={key}
            className={`${cfg.color} border rounded-xl p-4 transition-transform hover:-translate-y-0.5`}
            data-testid={`kpi-${key}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-2 h-2 rounded-full ${cfg.dot}`}></span>
              <p className={`text-xs font-bold uppercase tracking-wide ${cfg.text}`}>{cfg.label}</p>
            </div>
            <p className={`text-3xl font-bold ${cfg.text}`}>{stats[key]?.toLocaleString('es-ES') || 0}</p>
          </div>
        ))}
      </div>

      {/* Weekly Planning Chart */}
      <div className="mb-5">
        <SemanaCargaChart />
      </div>

      {/* Charts & Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

        {/* Por Zona */}
        <Card className="border-slate-200 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Por Zona
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.porLugar)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 8)
                .map(([lugar, count]) => (
                  <div key={lugar}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium text-slate-700 truncate max-w-[160px]">{lugar}</span>
                      <span className="font-bold text-slate-900">{count.toLocaleString('es-ES')}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-teal-500 rounded-full transition-all duration-500"
                        style={{ width: `${(count / maxLugar) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Por Canal */}
        <Card className="border-slate-200 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Globe className="w-4 h-4" /> Por Canal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.porProcedencia)
                .sort((a, b) => b[1] - a[1])
                .map(([proc, count]) => {
                  const pct = Math.round((count / totalProcedencia) * 100);
                  return (
                    <div key={proc}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-slate-700 flex items-center gap-2 capitalize">
                          {CANAL_ICONS[proc?.toLowerCase()] || <Globe className="w-4 h-4" />}
                          {proc}
                        </span>
                        <span className="text-slate-500 text-xs">{pct}% · {count.toLocaleString('es-ES')}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-400 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>

        {/* Acceso Rápido */}
        <Card className="border-slate-200 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">
              Acceso Rápido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {[
                { to: '/admin/tickets', icon: Ticket, label: 'Bandeja de Tickets' },
                { to: '/admin/rutas', icon: Route, label: 'Gestión de Rutas' },
                { to: '/admin/mapa', icon: Map, label: 'Mapa de Recogidas' },
                ...(isAdmin ? [{ to: '/admin/vehiculos', icon: Truck, label: 'Vehículos' }] : []),
              ].map(({ to, icon: Icon, label }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-teal-50 hover:text-teal-700 transition-colors group"
                  data-testid={`quick-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-4 h-4 text-slate-400 group-hover:text-teal-500" />
                  {label}
                </Link>
              ))}
              <button
                onClick={() => setShowExportModal(true)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-green-50 hover:text-green-700 transition-colors group"
                data-testid="quick-export-btn"
              >
                <Download className="w-4 h-4 text-slate-400 group-hover:text-green-500" />
                Exportar a Excel
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowExportModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="export-modal">
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Download className="w-5 h-5 text-teal-600" />
              Exportar Recogidas a Excel
            </h2>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700">Fecha Desde</Label>
                <Input type="date" value={exportFilters.fecha_desde} onChange={(e) => setExportFilters({ ...exportFilters, fecha_desde: e.target.value })} />
              </div>
              <div>
                <Label className="text-slate-700">Fecha Hasta</Label>
                <Input type="date" value={exportFilters.fecha_hasta} onChange={(e) => setExportFilters({ ...exportFilters, fecha_hasta: e.target.value })} />
              </div>
              <div>
                <Label className="text-slate-700">Estado (opcional)</Label>
                <select value={exportFilters.estado} onChange={(e) => setExportFilters({ ...exportFilters, estado: e.target.value })} className="w-full p-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Todos los estados</option>
                  {estados.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-slate-700">Zona (opcional)</Label>
                <select value={exportFilters.lugar} onChange={(e) => setExportFilters({ ...exportFilters, lugar: e.target.value })} className="w-full p-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Todas las zonas</option>
                  {zonas.map(z => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowExportModal(false)}>Cancelar</Button>
              <Button onClick={handleExport} className="bg-teal-600 hover:bg-teal-700">
                <Download className="w-4 h-4 mr-2" /> Descargar Excel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
